import { useState, useEffect, useRef } from "react";
import { Clock } from "lucide-react";

interface IntermediacaoCountdownProps {
  status: string;
  lastActivityAt: number; // timestamp ms
  openedAt?: number; // timestamp ms — when dialog was opened for this intermediation
  onTimeExpired?: () => void;
}

const TOTAL_SECONDS = 15 * 60;

const PAUSED_STATUSES = [
  "aguardando_intermediador",
  "aguardando_confirmacao_recebimento",
];

const HIDDEN_STATUSES = [
  "aguardando_pagamento_anunciante",
  "aguardando_confirmacao_pagamento",
  "concluido",
  "cancelado",
  "recusado",
];

const IntermediacaoCountdown = ({ status, lastActivityAt, openedAt, onTimeExpired }: IntermediacaoCountdownProps) => {
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiredFiredRef = useRef(false);

  const isPaused = PAUSED_STATUSES.includes(status);
  const isHidden = HIDDEN_STATUSES.includes(status);

  // Effective activity reference: the most recent of last message activity and when
  // the user opened this chat. This prevents auto-cancellation of stale chats
  // immediately upon opening — the user always gets the full window once they're viewing.
  const effectiveActivityAt = Math.max(lastActivityAt || 0, openedAt || 0);

  // Track elapsed time from effective activity while not paused
  const pausedAtRef = useRef<number | null>(null);
  const accumulatedPauseRef = useRef(0);

  // Reset accumulated pause on activity change (new message/event or chat reopened)
  useEffect(() => {
    accumulatedPauseRef.current = 0;
    pausedAtRef.current = null;
    expiredFiredRef.current = false;
  }, [effectiveActivityAt]);

  // Handle pause tracking
  useEffect(() => {
    if (isPaused) {
      if (!pausedAtRef.current) {
        pausedAtRef.current = Date.now();
      }
    } else {
      if (pausedAtRef.current) {
        accumulatedPauseRef.current += Date.now() - pausedAtRef.current;
        pausedAtRef.current = null;
      }
    }
  }, [isPaused]);

  useEffect(() => {
    if (isHidden) return;

    const tick = () => {
      if (isPaused) {
        return;
      }
      const now = Date.now();
      const currentPause = pausedAtRef.current ? (now - pausedAtRef.current) : 0;
      const totalPause = accumulatedPauseRef.current + currentPause;
      const elapsed = Math.floor((now - effectiveActivityAt - totalPause) / 1000);
      const left = Math.max(0, TOTAL_SECONDS - elapsed);
      setRemaining(left);

      if (left === 0 && !expiredFiredRef.current) {
        expiredFiredRef.current = true;
        onTimeExpired?.();
      }
    };

    tick();
    intervalRef.current = setInterval(tick, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [effectiveActivityAt, isPaused, isHidden, onTimeExpired]);

  if (isHidden) return null;

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const isLow = remaining <= 120;
  const isVeryLow = remaining <= 60;

  return (
    <div
      className={`flex items-center justify-center gap-1.5 py-1.5 px-3 text-[10px] font-pixel transition-colors ${
        isPaused
          ? "bg-blue-500/10 text-blue-400"
          : isVeryLow
          ? "bg-destructive/15 text-destructive animate-pulse"
          : isLow
          ? "bg-orange-500/15 text-orange-400"
          : "bg-primary/10 text-primary"
      }`}
    >
      <Clock className="w-3 h-3" />
      {isPaused ? (
        <span>Tempo pausado — aguardando ação</span>
      ) : (
        <span>
          Tempo restante: {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
        </span>
      )}
    </div>
  );
};

export default IntermediacaoCountdown;
